import {
  type CreatePackageBatchRequest,
  type CreatePackageVariantRequest,
  type PackageBatchOut,
  type PackageBatchStatus,
} from '@utazzo/shared';
import { UzButton, UzForm, UzFormField, UzInput, UzNumberInput, UzSelect } from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useEffect } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import {
  computeReturnDate,
  emptyTravellerRow,
  travellerRowToVariant,
  variantToTravellerRow,
  type TravellerOptionRow,
} from './package-batch-utils';
import { PackageFormPanel } from './PackageFormPanel';

const STATUS_OPTIONS: ReadonlyArray<{ value: PackageBatchStatus; label: string }> = [
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'FULL', label: 'Full' },
];

const MONEY_2DP_RE = /^\d*(\.\d{0,2})?$/;

interface FormValues {
  name: string;
  departureDate: string;
  returnDate: string;
  capacity: number | null;
  status: PackageBatchStatus;
  travellerOptions: TravellerOptionRow[];
}

export type BatchFormSubmit = {
  batch: CreatePackageBatchRequest;
  variants: CreatePackageVariantRequest[];
};

interface Props {
  mode: 'create' | 'edit';
  item?: PackageBatchOut;
  pending: boolean;
  numberOfDays: number;
  hasItinerary: boolean;
  onClose: () => void;
  onSubmit?: (payload: BatchFormSubmit) => Promise<void>;
  onSubmitDraft?: (payload: BatchFormSubmit) => void;
}

const toDefaults = (mode: 'create' | 'edit', item?: PackageBatchOut): FormValues =>
  mode === 'edit' && item
    ? {
        name: item.name,
        departureDate: item.departureDate,
        returnDate: item.returnDate ?? '',
        capacity: item.capacity,
        status: item.status,
        travellerOptions:
          item.variants.length > 0
            ? item.variants.map(variantToTravellerRow)
            : [emptyTravellerRow()],
      }
    : {
        name: '',
        departureDate: '',
        returnDate: '',
        capacity: null,
        status: 'OPEN',
        travellerOptions: [emptyTravellerRow()],
      };

export const PackageBatchFormPanel = ({
  mode,
  item,
  pending,
  numberOfDays,
  hasItinerary,
  onClose,
  onSubmit,
  onSubmitDraft,
}: Props): React.JSX.Element => {
  const form = useForm<FormValues>({ defaultValues: toDefaults(mode, item) });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'travellerOptions',
  });

  const departureDate = useWatch({ control: form.control, name: 'departureDate' });

  useEffect(() => {
    form.reset(toDefaults(mode, item));
  }, [mode, item, form]);

  useEffect(() => {
    if (!hasItinerary || !departureDate) return;
    form.setValue('returnDate', computeReturnDate(departureDate, numberOfDays), {
      shouldDirty: true,
    });
  }, [hasItinerary, departureDate, numberOfDays, form]);

  const submit = form.handleSubmit(async (values) => {
    const travellerOptions = values.travellerOptions
      .filter((r) => r.name.trim() !== '' || r.priceRupees.trim() !== '')
      .map(travellerRowToVariant)
      .filter((v) => v.name && v.price);

    if (travellerOptions.length === 0) {
      form.setError('travellerOptions.0.name', {
        type: 'manual',
        message: 'Add at least one traveller option with name and price',
      });
      form.setError('travellerOptions.0.priceRupees', {
        type: 'manual',
        message: 'Required for website pricing',
      });
      return;
    }

    const payload: BatchFormSubmit = {
      batch: {
        name: values.name.trim(),
        departureDate: values.departureDate,
        returnDate: values.returnDate.trim() === '' ? null : values.returnDate,
        capacity: values.capacity,
        status: values.status,
      },
      variants: travellerOptions,
    };

    if (onSubmitDraft) {
      onSubmitDraft(payload);
      return;
    }
    await onSubmit?.(payload);
  });

  return (
    <PackageFormPanel
      title={mode === 'create' ? 'Add batch' : 'Edit batch'}
      description="Departure batch with traveller pricing options (e.g. Tempo Traveller, SUV)."
      pending={pending}
      submitLabel={mode === 'create' ? 'Add batch' : 'Save batch'}
      onClose={onClose}
      onSubmit={submit}
    >
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-5">
          <UzFormField name="name">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string) ?? ''}
                label="Batch name"
                placeholder="e.g. March 2026 departure"
                error={error}
                required
                disabled={pending}
              />
            )}
          </UzFormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <UzFormField name="departureDate">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  type="date"
                  value={(field.value as string) ?? ''}
                  label="Departure date"
                  error={error}
                  required
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="returnDate">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  type="date"
                  value={(field.value as string) ?? ''}
                  label="Return date"
                  hint={
                    hasItinerary
                      ? `Auto from ${numberOfDays}D itinerary`
                      : 'Add itinerary days to auto-calculate, or enter manually'
                  }
                  error={error}
                  disabled={pending || hasItinerary}
                />
              )}
            </UzFormField>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <UzFormField name="capacity">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Capacity (optional)"
                  precision={0}
                  min={1}
                  value={field.value === null || field.value === undefined ? '' : field.value}
                  onValueChange={(v) => field.onChange(v ?? null)}
                  hint="Maximum seats for this batch"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="status">
              {({ field, error }) => (
                <UzSelect
                  name={field.name}
                  label="Status"
                  value={(field.value as PackageBatchStatus) ?? 'OPEN'}
                  onValueChange={field.onChange}
                  options={STATUS_OPTIONS}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>

          <div className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50/40 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold text-zinc-900">Traveller options</h4>
                <p className="text-xs text-zinc-500">
                  Vehicle or occupancy type with price and optional discount.
                </p>
              </div>
              <UzButton
                type="button"
                size="sm"
                variant="ghost"
                disabled={pending}
                onClick={() => append(emptyTravellerRow())}
              >
                <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
                Add option
              </UzButton>
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid gap-3 rounded-md border border-zinc-200 bg-white p-3 sm:grid-cols-[minmax(0,1fr)_120px_120px_auto]"
                >
                  <UzFormField name={`travellerOptions.${index}.name`}>
                    {({ field: f, error }) => (
                      <UzInput
                        {...f}
                        value={(f.value as string) ?? ''}
                        label={index === 0 ? 'Option' : undefined}
                        placeholder="e.g. Tempo Traveller"
                        error={error}
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                  <UzFormField name={`travellerOptions.${index}.priceRupees`}>
                    {({ field: f, error }) => (
                      <UzInput
                        name={f.name}
                        value={(f.value as string) ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!MONEY_2DP_RE.test(v)) return;
                          f.onChange(v);
                        }}
                        label={index === 0 ? 'Price (₹)' : undefined}
                        inputMode="decimal"
                        placeholder="25000"
                        error={error}
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                  <UzFormField name={`travellerOptions.${index}.discountRupees`}>
                    {({ field: f, error }) => (
                      <UzInput
                        name={f.name}
                        value={(f.value as string) ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!MONEY_2DP_RE.test(v)) return;
                          f.onChange(v);
                        }}
                        label={index === 0 ? 'Discount (₹)' : undefined}
                        inputMode="decimal"
                        placeholder="1000"
                        error={error}
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                  <div className={index === 0 ? 'pt-6' : 'pt-0 sm:pt-0'}>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-red-600 disabled:opacity-40"
                      aria-label="Remove traveller option"
                      disabled={pending || fields.length <= 1}
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </UzForm>
    </PackageFormPanel>
  );
};
